// Library Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';

// Local Imports
import endPoints from '../../constant';
import axiosInstance from '../../http';

export interface InitialStateProps {
  loginUser?: any;
  loading?: boolean;
  error?: string | null;
  profile?: any;
  profileLoading?: boolean;
}

const initialState: InitialStateProps = {
  loginUser: [],
  profile: [],
  loading: false,
  error: null,
  profileLoading: false,
};

// Login User API Calling
export const loginUserAPI = createAsyncThunk(
  'auth/loginUser',
  async (values: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(endPoints.login, {
        ...values,
      });
      return data;
    } catch (error: any) {
      if (error?.message === 'Network Error') {
        return rejectWithValue(
          error?.message + '. Please check your Internet Connection!'
        );
      } else {
        return rejectWithValue(error?.response?.data?.error?.message);
      }
    }
  }
);

// Get UserProfile API Calling
export const profileAPI = createAsyncThunk(
  'dashboard/profile',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(endPoints.profile);
      return data;
    } catch (error: any) {
      if (error?.message === 'Network Error') {
        return rejectWithValue(
          error?.message + '. Please check your Internet Connection!'
        );
      } else {
        return rejectWithValue(error?.response?.data?.error?.message);
      }
    }
  }
);

// AddNewProfile API Calling
export const addNewProfileAPI = createAsyncThunk(
  'dashboard/addNewProfile',
  async (values: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(
        endPoints.profile,
        values.payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return data;
    } catch (error: any) {
      if (error?.message === 'Network Error') {
        return rejectWithValue(
          error?.message + '. Please check your Internet Connection!'
        );
      } else {
        return rejectWithValue(error?.response?.data?.error?.message);
      }
    }
  }
);

// Update Profile API Calling
export const updateProfileAPI = createAsyncThunk(
  'dashboard/updateProfile',
  async (values: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put(
        `${endPoints.profile}/${values.id}`,
        values.payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return data;
    } catch (error: any) {
      if (error?.message === 'Network Error') {
        return rejectWithValue(
          error?.message + '. Please check your Internet Connection!'
        );
      } else {
        return rejectWithValue(error?.response?.data?.error?.message);
      }
    }
  }
);

// Login User Slice
export const loginUserSlice = createSlice({
  name: 'auth/loginUser',
  initialState,
  reducers: {
    logout: (state) => {
      state.loginUser = [];
    },
    addProfile: (state, action) => {
      state.profile = {
        data: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUserAPI.pending, (state) => {
        state.loading = true;
        state.profile = [];
        state.error = null;
      })
      .addCase(loginUserAPI.fulfilled, (state, action) => {
        state.loading = false;
        state.loginUser = action.payload;
        state.error = null;
      })
      .addCase(loginUserAPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addNewProfileAPI.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewProfileAPI.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(addNewProfileAPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(profileAPI.pending, (state) => {
        state.profileLoading = true;
        state.error = null;
      })
      .addCase(profileAPI.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(profileAPI.rejected, (state, action) => {
        state.profileLoading = false;
        state.profile = [];
        state.error = action.payload as string;
      })
      .addCase(updateProfileAPI.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfileAPI.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(updateProfileAPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(PURGE, (state) => {
        state.loginUser = [];
      });
  },
});

// Action creators are generated for each case reducer function
export const { logout, addProfile } = loginUserSlice.actions;

export default loginUserSlice.reducer;
